import React, { useState, useEffect, useCallback } from 'react';
import { getSplitSummary, getBeneficiarySummary, getSplitTransactions, exportSplitReport, getSplitSummarySandbox, getBeneficiarySummarySanbox, getSplitTransactionsSanbox, exportSplitReportSandbox } from '../../services/transaction';
import { useAuth } from '../../context/AuthContextProvider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const SplitReport = () => {
    const { globalProfile } = useAuth();

    const getSplitSummaryFunc = globalProfile === 'TEST' ? getSplitSummarySandbox : getSplitSummary;
    const getBeneficiarySummaryFunc = globalProfile === 'TEST' ? getBeneficiarySummarySanbox : getBeneficiarySummary;
    const getSplitTransactionsFunc = globalProfile === 'TEST' ? getSplitTransactionsSanbox : getSplitTransactions;
    const exportSplitReportFunc = globalProfile === 'TEST' ? exportSplitReportSandbox : exportSplitReport;

    // --- Constants & Palette ---
    const PALETTE = [
        { bg: '#fff4e6', color: '#f58220' },
        { bg: '#ebfbee', color: '#0ca678' },
        { bg: '#fff4e6', color: '#f76707' },
        { bg: '#f3f0ff', color: '#7048e8' },
        { bg: '#fff5f5', color: '#e03131' },
        { bg: '#e8faf5', color: '#099268' },
        { bg: '#fff9db', color: '#e67700' },
        { bg: '#f0f4ff', color: '#3b5bdb' },
    ];

    const fmtDate = (d) => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };
    const initials = (n) => n ? n.split(' ').slice(0, 2).map(p => p[0]).join('') : '';

    // --- State ---
    const [period, setPeriod] = useState('all');
    const [customRange, setCustomRange] = useState({ from: '', to: '' });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [schemeFilter, setSchemeFilter] = useState('');
    const [splitFilter, setSplitFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState(null);

    const [summary, setSummary] = useState({
        totalTransactions: 0,
        totalVolume: 0,
        successful: 0,
        pending: 0,
        withSplitConfig: 0
    });
    const [beneficiariesData, setBeneficiariesData] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [isLoadingTable, setIsLoadingTable] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const pageSize = 10;

    const fetchReportData = useCallback(async () => {
        let dateRangeMapped = 'AllTime';
        if (period === 'today') dateRangeMapped = 'today';
        else if (period === '7d') dateRangeMapped = '7days';
        else if (period === '30d') dateRangeMapped = '30days';
        else if (period === '90d') dateRangeMapped = '90days';
        else if (period === 'all') dateRangeMapped = 'AllTime';
        else if (period === 'custom') dateRangeMapped = 'custom';

        const params = {
            PageNumber: currentPage,
            PageSize: pageSize,
            Search: search,
            Status: statusFilter,
            Scheme: schemeFilter,
            SplitType: splitFilter === 'none' ? 'None' : (splitFilter === 'simple' ? 'Simple' : (splitFilter === 'complex' ? 'Complex' : '')),
            DateRange: dateRangeMapped,
        };

        if (period === 'custom' && customRange.from && customRange.to) {
            params.From = customRange.from;
            params.To = customRange.to;
        }

        setIsLoadingStats(true);
        setIsLoadingTable(true);

        try {
            const [summaryRes, beneRes, txnRes] = await Promise.all([
                getSplitSummaryFunc(params),
                getBeneficiarySummaryFunc(params),
                getSplitTransactionsFunc(params)
            ]);

            if (summaryRes?.success && summaryRes.data) {
                setSummary(summaryRes.data);
            }
            if (beneRes?.success && beneRes.data?.beneficiaries) {
                setBeneficiariesData(beneRes.data.beneficiaries);
            }
            if (txnRes?.success && txnRes.data) {
                setTransactions(txnRes.data.result || []);
                setTotalRecords(txnRes.data.totalRecords ?? 0);
            }
        } catch (error) {
            console.error("Failed to fetch split reporting data", error);
        } finally {
            setIsLoadingStats(false);
            setIsLoadingTable(false);
        }
    }, [currentPage, pageSize, search, statusFilter, schemeFilter, splitFilter, period, customRange, getSplitSummaryFunc, getBeneficiarySummaryFunc, getSplitTransactionsFunc]);

    useEffect(() => {
        if (period === 'custom' && (!customRange.from || !customRange.to)) {
            return;
        }
        fetchReportData();
    }, [fetchReportData]);

    const exportCSV = async () => {
        setIsExporting(true);
        try {
            let dateRangeMapped = 'AllTime';
            if (period === 'today') dateRangeMapped = 'today';
            else if (period === '7d') dateRangeMapped = '7days';
            else if (period === '30d') dateRangeMapped = '30days';
            else if (period === '90d') dateRangeMapped = '90days';
            else if (period === 'all') dateRangeMapped = 'AllTime';
            else if (period === 'custom') dateRangeMapped = 'custom';

            const params = {
                PageNumber: currentPage,
                PageSize: pageSize,
                Search: search,
                Status: statusFilter,
                Scheme: schemeFilter,
                SplitType: splitFilter === 'none' ? 'None' : (splitFilter === 'simple' ? 'Simple' : (splitFilter === 'complex' ? 'Complex' : '')),
                DateRange: dateRangeMapped,
            };

            if (period === 'custom' && customRange.from && customRange.to) {
                params.From = customRange.from;
                params.To = customRange.to;
            }

            const blob = await exportSplitReportFunc(params);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const a = document.createElement('a');
            a.setAttribute('hidden', '');
            a.setAttribute('href', url);
            a.setAttribute('download', 'split_report.csv');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error("Export failed", error);
        } finally {
            setIsExporting(false);
        }
    };

    const rowExpansionTemplate = (t) => {
        if (!t.splitType || t.splitType === 'None') return null;
        return (
            <div className="p-3 bg-[#fafafa]">
                <div className="split-block">
                    <div className="split-block-head">
                        <span className="label-text">Split Allocation ({t.splitType})</span>
                        {t.triggerKeyword && (
                            <span className="bene-rule-tag" style={{ background: '#eee', color: '#666', marginLeft: 8 }}>
                                Keyword: {t.triggerKeyword}
                            </span>
                        )}
                    </div>
                    <div className="split-items">
                        {t.allocations?.map((sub, idx) => (
                            <div key={idx} className="split-item">
                                <div
                                    className="split-color-dot"
                                    style={{ background: PALETTE[idx % PALETTE.length].color }}
                                ></div>
                                <div>
                                    <div className="split-item-name">{sub.beneficiaryName}</div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                                        {sub.allocationType === 'Percentage'
                                            ? `${sub.percentage}%`
                                            : sub.allocationType === 'Flat'
                                            ? 'Flat Amount'
                                            : 'Residual'}
                                    </div>
                                </div>
                                <div className="split-item-amount">
                                    ₦{(sub.allocatedAmount || 0).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="split-report-container">
            <style>{`
        :root {
          --bg: #fafafa; --white: #ffffff; --surface: #f0efe9; --border: #e2e0d8;
          --border-dark: #ccc9bf; --accent: #f58220; --accent-light: #fff4e6;
          --accent2: #0ca678; --accent2-light: #ebfbee; --accent3: #bb4430;
          --accent3-light: #fff5f5; --accent4: #f76707; --accent4-light: #fff4e6;
          --accent5: #7048e8; --accent5-light: #f3f0ff; --text: #030229;
          --text-mid: #4a4a6a; --text-dim: #8888aa; --text-muted: #b0b0c8;
          --shadow: 0 1px 4px rgba(26,26,46,0.07), 0 4px 16px rgba(26,26,46,0.05);
          --shadow-md: 0 2px 8px rgba(26,26,46,0.09), 0 8px 32px rgba(26,26,46,0.07);
          --radius: 14px; --radius-sm: 9px;
        }
        .split-report-container { background: var(--bg); color: var(--text); font-family: 'Lato', sans-serif; min-height: 100vh; padding: 24px; }
        .page-title { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 16px; margin-bottom: 28px; }
        .page-title h1 { font-family: 'Lato', sans-serif; font-size: 1.75rem; font-weight: 900; letter-spacing: -0.03em; line-height: 1.15; }
        .page-title p { color: var(--text-dim); font-size: 0.84rem; margin-top: 5px; }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin-bottom: 28px; }
        .stat-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px 20px; box-shadow: var(--shadow); }
        .stat-label { font-size: 0.68rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
        .stat-val { font-family: 'Lato', sans-serif; font-size: 1.5rem; font-weight: 900; letter-spacing: -0.02em; line-height: 1; min-height: 24px; }
        .stat-sub { font-size: 0.72rem; color: var(--text-dim); margin-top: 5px; }
        .c-blue { color: var(--accent); } .c-green { color: var(--accent2); } .c-orange { color: var(--accent4); } .c-red { color: var(--accent3); } .c-purple { color: var(--accent5); }
        .section-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
        .section-title { font-family: 'Lato', sans-serif; font-size: 1.1rem; font-weight: 700; color: var(--text); letter-spacing: -0.02em; }
        .section-title small { font-family: 'Lato', sans-serif; font-size: 0.75rem; font-weight: 500; color: var(--text-dim); margin-left: 8px; }
        .period-filters { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .period-btn { padding: 6px 14px; border-radius: 20px; border: 1.5px solid var(--border); background: var(--white); font-family: 'Lato', sans-serif; font-size: 0.75rem; font-weight: 600; color: var(--text-dim); cursor: pointer; transition: all 0.15s; }
        .period-btn:hover { border-color: var(--accent); color: var(--accent); }
        .period-btn.active { background: var(--accent); border-color: var(--accent); color: #fff; }
        .custom-date-wrap { display: flex; gap: 6px; align-items: center; }
        .custom-date-wrap input[type="date"] { background: var(--white); border: 1.5px solid var(--border); color: var(--text); font-family: 'Lato', sans-serif; font-size: 0.75rem; padding: 6px 10px; border-radius: 8px; outline: none; transition: border-color 0.15s; cursor: pointer; }
        .bene-cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; margin-bottom: 36px; }
        .bene-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); padding: 20px; box-shadow: var(--shadow); transition: box-shadow 0.18s, transform 0.18s; animation: fadeUp 0.22s ease both; }
        .bene-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .bene-avatar { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Lato', sans-serif; font-size: 1rem; font-weight: 700; flex-shrink: 0; margin-right: 12px; }
        .bene-name { font-size: 0.88rem; font-weight: 600; color: var(--text); line-height: 1.2; }
        .bene-total { font-family: 'Lato', sans-serif; font-size: 1.4rem; font-weight: 900; letter-spacing: -0.02em; color: var(--text); }
        .bene-bar-track { height: 5px; border-radius: 3px; background: var(--surface); overflow: hidden; margin-top: 14px; }
        .bene-bar-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }
        .bene-pct { font-size: 0.7rem; color: var(--text-dim); text-align: right; margin-top: 4px; }
        .bene-rule-tag { display: inline-block; font-size: 0.65rem; font-weight: 600; padding: 2px 8px; border-radius: 20px; margin-top: 8px; }
        .filters-bar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
        .filter-input { background: var(--white); border: 1.5px solid var(--border); color: var(--text); font-family: 'Lato', sans-serif; font-size: 0.82rem; padding: 9px 13px; border-radius: var(--radius-sm); outline: none; transition: border-color 0.15s; width: 220px; }
        select.filter-input { appearance: none; cursor: pointer; width: auto; padding-right: 32px; background-repeat: no-repeat; background-position: right 12px center; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238888aa' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); }
        .table-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); overflow: hidden; margin-bottom: 16px; }
        .split-block { background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; margin-top: 4px; margin-bottom: 4px; }
        .split-block-head { display: flex; align-items: center; gap: 8px; padding: 8px 14px; background: var(--surface); border-bottom: 1px solid var(--border); }
        .split-items { display: flex; flex-wrap: wrap; }
        .split-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-right: 1px solid var(--border); min-width: 200px; flex: 1; }
        .split-item-amount { margin-left: auto; font-family: 'Lato', sans-serif; font-weight: 700; color: var(--accent2); }
        .split-toggle-btn { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; border: 1.5px solid var(--border); background: var(--white); font-size: 0.72rem; font-weight: 600; color: var(--text-dim); cursor: pointer; transition: all 0.16s; }
        .split-toggle-btn.has-split { border-color: rgba(26,86,219,0.25); color: var(--accent); background: var(--accent-light); }
        .split-toggle-btn.open { background: var(--accent); border-color: var(--accent); color: #fff; }
        .status-badge { display: inline-flex; align-items: center; justify-content: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 0.68rem; font-weight: 700; }
        .s-approved, .s-successful { background: var(--accent2-light); color: var(--accent2); }
        .s-pending { background: var(--accent4-light); color: var(--accent4); }
        .s-failed { background: var(--accent3-light); color: var(--accent3); }
        .scheme-badge { font-size: 0.68rem; font-weight: 700; padding: 2px 8px; border-radius: 5px; }
        .sc-visa { background: var(--accent-light); color: var(--accent); }
        .sc-mastercard, .sc-mc { background: var(--accent3-light); color: var(--accent3); }
        .sc-verve { background: var(--accent2-light); color: var(--accent2); }
        .export-btn { background: var(--accent); color: #fff; border: none; font-size: 0.8rem; font-weight: 600; padding: 7px 16px; border-radius: 8px; cursor: pointer; margin-left: auto; }
        .loading-state { padding: 40px; text-align: center; color: var(--text-dim); }
        .split-color-dot { width: 8px; height: 8px; border-radius: 50%; }
        .no-bene { padding: 30px; text-align: center; color: var(--text-dim); background: var(--white); border: 1px dashed var(--border); border-radius: var(--radius); }
        .label-text { font-size: 0.75rem; font-weight: 700; }
        .split-item-name { font-size: 0.82rem; font-weight: 600; color: var(--text); }
      `}</style>

            <div className="page-title">
                <div>
                    <h1>Transaction Report</h1>
                    <p>Transaction log with split allocation details per transaction</p>
                </div>
                <button className="export-btn" onClick={exportCSV} disabled={isExporting}>
                    {isExporting ? 'Exporting...' : '↓ Export CSV'}
                </button>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-label">📋 Transactions</div>
                    <div className="stat-val c-blue">{isLoadingStats ? '...' : summary.totalTransactions}</div>
                    <div className="stat-sub">All records</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">💰 Total Volume</div>
                    <div className="stat-val c-green">{isLoadingStats ? '...' : `₦${(summary.totalVolume || 0).toLocaleString()}`}</div>
                    <div className="stat-sub">Successful only</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">✅ Successful</div>
                    <div className="stat-val c-green">{isLoadingStats ? '...' : summary.successful}</div>
                    <div className="stat-sub">Transactions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">⌛ Pending</div>
                    <div className="stat-val c-orange">{isLoadingStats ? '...' : summary.pending}</div>
                    <div className="stat-sub">Transactions</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">🔀 Split</div>
                    <div className="stat-val c-purple">{isLoadingStats ? '...' : summary.withSplitConfig}</div>
                    <div className="stat-sub">With split config</div>
                </div>
            </div>

            <div className="section-head">
                <div className="section-title">
                    Beneficiary Cumulative Summary{' '}
                    <small>
                        {period === 'all'
                            ? 'All time'
                            : period === 'custom'
                            ? `${customRange.from} to ${customRange.to}`
                            : period}
                    </small>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div className="period-filters">
                        {['all', 'today', '7d', '30d', '90d', 'custom'].map((p) => (
                            <button
                                key={p}
                                className={`period-btn ${period === p ? 'active' : ''}`}
                                onClick={() => setPeriod(p)}
                            >
                                {p === 'all'
                                    ? 'All Time'
                                    : p === '7d'
                                    ? '7 Days'
                                    : p === '30d'
                                    ? '30 Days'
                                    : p === '90d'
                                    ? '90 Days'
                                    : p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                        ))}
                    </div>
                    {period === 'custom' && (
                        <div className="custom-date-wrap">
                            <input
                                type="date"
                                value={customRange.from}
                                onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })}
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={customRange.to}
                                onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="bene-cards-grid">
                {isLoadingStats ? (
                    <div className="loading-state" style={{ gridColumn: '1 / -1' }}>
                        Loading beneficiary data...
                    </div>
                ) : beneficiariesData.length === 0 ? (
                    <div className="no-bene" style={{ gridColumn: '1 / -1' }}>
                        No beneficiary data for this period.
                    </div>
                ) : (
                    beneficiariesData.map((bene, idx) => {
                        const name = bene.beneficiaryName;
                        const data = {
                            count: bene.transactionCount,
                            total: bene.totalReceived,
                            keyword: bene.keywords?.join(', '),
                        };
                        const pal = PALETTE[idx % PALETTE.length];
                        const pct = bene.percentageOfTotal || 0;
                        return (
                            <div key={name} className="bene-card">
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                    <div className="bene-avatar" style={{ background: pal.bg, color: pal.color }}>
                                        {initials(name)}
                                    </div>
                                    <div>
                                        <div className="bene-name">{name}</div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                                            {data.count} txns
                                        </div>
                                    </div>
                                </div>
                                <div className="bene-total">₦{(data.total || 0).toLocaleString()}</div>
                                <div className="bene-bar-wrap">
                                    <div className="bene-bar-track">
                                        <div className="bene-bar-fill" style={{ width: `${pct}%`, background: pal.color }}></div>
                                    </div>
                                    <div className="bene-pct">{pct}% of total successful volume</div>
                                </div>
                                {data.keyword && (
                                    <span className="bene-rule-tag" style={{ background: pal.bg, color: pal.color }}>
                                        Keyword(s): {data.keyword}
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="section-head" style={{ marginTop: 8 }}>
                <div className="section-title">Transactions</div>
            </div>

            <div className="filters-bar">
                <input
                    type="text"
                    className="filter-input"
                    placeholder="Search name, ref, description…"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                    }}
                />
                <select
                    className="filter-input"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    <option value="">All Statuses</option>
                    <option value="APPROVED">Approved / Successful</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                </select>
                <select
                    className="filter-input"
                    value={schemeFilter}
                    onChange={(e) => {
                        setSchemeFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    <option value="">All Schemes</option>
                    <option value="Visa">Visa</option>
                    <option value="MC">Mastercard</option>
                    <option value="Verve">Verve</option>
                    <option value="Amex">Amex</option>
                </select>
                <select
                    className="filter-input"
                    value={splitFilter}
                    onChange={(e) => {
                        setSplitFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    <option value="">All Split Types</option>
                    <option value="simple">Simple</option>
                    <option value="complex">Complex</option>
                    <option value="none">No Split</option>
                </select>
            </div>

            <div className="table-card">
                <DataTable
                    value={transactions}
                    lazy
                    paginator
                    first={(currentPage - 1) * pageSize}
                    rows={pageSize}
                    totalRecords={totalRecords}
                    onPage={(e) => setCurrentPage(e.page + 1)}
                    loading={isLoadingTable}
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="id"
                    emptyMessage="No transactions found."
                    className="p-datatable-sm"
                    responsiveLayout="scroll"
                >
                    <Column field="id" header="#" />
                    <Column field="description" header="Description" body={(t) => <b>{t.description}</b>} />
                    <Column field="amount" header="Amount" body={(t) => <b>₦{(t.amount || 0).toLocaleString()}</b>} />
                    <Column field="currency" header="Currency" />
                    <Column field="initiated" header="Date" body={(t) => fmtDate(t.initiated)} />
                    <Column
                        field="status"
                        header="Status"
                        body={(t) => <span className={`status-badge s-${(t.status || '').toLowerCase()}`}>{t.status}</span>}
                    />
                    <Column field="statusDescription" header="Status Description" body={(t) => <small>{t.statusDescription || '---'}</small>} />
                    <Column field="maskedPan" header="Masked PAN" body={(t) => <code>{t.maskedPan}</code>} />
                    <Column field="scheme" header="Scheme" body={(t) => <span className={`scheme-badge sc-${(t.scheme || '').toLowerCase()}`}>{t.scheme}</span>} />
                    <Column field="customerName" header="Customer Name" />
                    <Column field="customerEmail" header="Email" />
                    <Column field="referenceId" header="Reference ID" body={(t) => <small>{t.referenceId}</small>} />
                    <Column
                        header="Split"
                        body={(t) => {
                            const hasSplit = t.splitType && t.splitType !== 'None';
                            const isExpanded = expandedRows && expandedRows[t.id];
                            return (
                                <button
                                    type="button"
                                    className={`split-toggle-btn ${hasSplit ? 'has-split' : ''} ${isExpanded ? 'open' : ''}`}
                                    onClick={() => {
                                        if (!hasSplit) return;
                                        const _expanded = { ...expandedRows };
                                        if (_expanded[t.id]) {
                                            delete _expanded[t.id];
                                        } else {
                                            _expanded[t.id] = true;
                                        }
                                        setExpandedRows(_expanded);
                                    }}
                                    disabled={!hasSplit}
                                >
                                    {hasSplit ? 'View Split' : 'No Split'} {hasSplit ? (isExpanded ? '▲' : '▼') : ''}
                                </button>
                            );
                        }}
                    />
                </DataTable>
            </div>
        </div>
    );
};

export default SplitReport;
